import React, { Component } from 'react';
import type {
  GetItemPropsParams,
  ItemContext,
  ItemRendererGetResizePropsReturnType,
  RealItemProps,
} from './Item';
import Item from './Item';
import { _get, arraysEqual, keyBy } from '../utility/generic';
import type { ItemDimensions } from '../utility/calendar';
import { getGroupOrders, getVisibleItems } from '../utility/calendar';
import type {
  TimelineContextValue,
  TimelineGroupBase,
  TimelineItemBase,
} from '../types';
import type { Moment } from 'moment/moment';

export type CanResize = boolean | 'left' | 'right' | 'both' | undefined;

const canResizeLeft = (
  item: {
    canResize?: CanResize;
  },
  canResize: CanResize,
) => {
  const value = _get(item, 'canResize') ?? canResize;
  return value === 'left' || value === 'both';
};

const canResizeRight = (
  item: {
    canResize?: CanResize;
  },
  canResize: CanResize,
) => {
  const value = _get(item, 'canResize') ?? canResize;
  return value === 'right' || value === 'both' || value === true;
};

export type ItemsProps<
  CustomGroup extends TimelineGroupBase,
  CustomItem extends TimelineItemBase,
> = {
  groups: CustomGroup[];
  items: CustomItem[];

  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  canvasTop: number;
  timeRatio: number;
  pixelsPerMs: number;

  dragSnap?: number;
  minResizeWidth?: number;
  selectedItem: null | string | number;

  canChangeGroup?: boolean;
  canMove?: boolean;
  canResize?: true | false | 'left' | 'right' | 'both';
  canSelect?: boolean;

  keys: {
    itemIdKey: keyof CustomItem;
    itemGroupKey: keyof CustomItem;
    itemTimeStartKey: keyof CustomItem;
    itemTimeEndKey: keyof CustomItem;
    itemTitleKey: keyof CustomItem;
    itemDivTitleKey: keyof CustomItem;
    groupIdKey: keyof CustomGroup;
  };

  moveResizeValidator?: (
    event: 'move' | 'resize',
    item: CustomItem,
    dragTime: number | Moment,
    resizeEdge?: null | 'left' | 'right',
  ) => number;
  itemSelect?: (
    item: string | number | null,
    clickType?: 'click' | 'touch',
    e?: React.MouseEvent | React.TouchEvent,
  ) => void;
  itemDrag?: (
    item: string | number,
    dragTime: number,
    newGroupOrder: number,
  ) => void;
  itemDrop?: (
    item: string | number,
    dragTime: number,
    newGroupOrder: number,
  ) => void;
  itemResizing?: (
    item: string | number,
    resizeTime: number,
    edge: null | 'left' | 'right',
  ) => void;
  itemResized?: (
    item: string | number,
    resizeTime: number,
    edge: null | 'left' | 'right',
    timeDelta: number,
  ) => void;

  onItemDoubleClick?: (item: string | number, e: React.MouseEvent) => void;
  onItemContextMenu?: (item: string | number, e: React.MouseEvent) => void;
  onItemTap?: (
    itemId: string | number,
    e: React.TouchEvent<HTMLElement>,
  ) => void;

  itemRenderer?: React.FunctionComponent<{
    item: CustomItem;
    timelineContext: TimelineContextValue;
    itemContext: ItemContext;
    getItemProps: (props: GetItemPropsParams) => RealItemProps;
    getResizeProps: () => ItemRendererGetResizePropsReturnType;
  }>;
  getTimelineContext: () => TimelineContextValue;
  selected?: (string | number)[];

  dimensionItems: ItemDimensions[];
  groupTops: number[];
  useResizeHandle?: boolean;
  scrollRef?: HTMLElement | null;
};

export default class Items<
  CustomGroup extends TimelineGroupBase,
  CustomItem extends TimelineItemBase,
> extends Component<ItemsProps<CustomGroup, CustomItem>> {
  static defaultProps = {
    selected: [],
  };

  shouldComponentUpdate(nextProps: ItemsProps<CustomGroup, CustomItem>) {
    return !(
      nextProps.keys === this.props.keys &&
      nextProps.canvasTimeStart === this.props.canvasTimeStart &&
      nextProps.canvasTimeEnd === this.props.canvasTimeEnd &&
      nextProps.canvasWidth === this.props.canvasWidth &&
      nextProps.selectedItem === this.props.selectedItem &&
      nextProps.selected === this.props.selected &&
      nextProps.dragSnap === this.props.dragSnap &&
      nextProps.minResizeWidth === this.props.minResizeWidth &&
      nextProps.canChangeGroup === this.props.canChangeGroup &&
      nextProps.canMove === this.props.canMove &&
      nextProps.canResize === this.props.canResize &&
      nextProps.canSelect === this.props.canSelect &&
      nextProps.canvasTop === this.props.canvasTop &&
      nextProps.timeRatio === this.props.timeRatio &&
      nextProps.pixelsPerMs === this.props.pixelsPerMs &&
      arraysEqual(nextProps.groups, this.props.groups) &&
      arraysEqual(nextProps.items, this.props.items) &&
      arraysEqual(nextProps.dimensionItems, this.props.dimensionItems)
    );
  }

  isSelected(item: CustomItem, itemIdKey: keyof CustomItem) {
    if (!this.props.selected) {
      return this.props.selectedItem === _get(item, itemIdKey);
    } else {
      const target = _get(item, itemIdKey) as string;
      return this.props.selected.includes(target);
    }
  }

  getVisibleItems(canvasTimeStart: number, canvasTimeEnd: number) {
    const { keys, items } = this.props;

    return getVisibleItems(items, canvasTimeStart, canvasTimeEnd, keys);
  }

  render() {
    const {
      canvasTimeStart,
      canvasTimeEnd,
      dimensionItems,
      canvasTop,
      keys,
      groups,
      canChangeGroup,
      canMove,
      canResize,
      canSelect,
      useResizeHandle,
      groupTops,
      canvasWidth,
      dragSnap,
      minResizeWidth,
      itemResizing,
      itemResized,
      moveResizeValidator,
      itemDrag,
      itemDrop,
      onItemDoubleClick,
      onItemContextMenu,
      onItemTap,
      itemSelect,
      itemRenderer,
      scrollRef,
      getTimelineContext,
      timeRatio,
      pixelsPerMs,
    } = this.props;
    const { itemIdKey, itemGroupKey } = keys;

    const groupOrders = getGroupOrders(groups, keys);
    const visibleItems = this.getVisibleItems(canvasTimeStart, canvasTimeEnd);
    const sortedDimensionItems = keyBy(dimensionItems, 'id');

    return (
      <div className="rct-items">
        {visibleItems
          .filter(
            (item) => sortedDimensionItems[_get(item, itemIdKey) as string],
          )
          .map((item) => (
            <Item
              key={_get(item, itemIdKey)}
              item={item}
              keys={keys}
              canvasTop={canvasTop}
              order={groupOrders[_get(item, itemGroupKey) as string]}
              dimensions={
                sortedDimensionItems[_get(item, itemIdKey) as string].dimensions
              }
              selected={this.isSelected(item, itemIdKey)}
              canChangeGroup={_get(item, 'canChangeGroup') ?? canChangeGroup}
              canMove={_get(item, 'canMove') ?? canMove}
              canResizeLeft={canResizeLeft(item, canResize)}
              canResizeRight={canResizeRight(item, canResize)}
              canSelect={_get(item, 'canSelect') ?? canSelect}
              useResizeHandle={useResizeHandle}
              groupTops={groupTops}
              canvasTimeStart={canvasTimeStart}
              canvasTimeEnd={canvasTimeEnd}
              canvasWidth={canvasWidth}
              dragSnap={dragSnap}
              minResizeWidth={minResizeWidth}
              onResizing={itemResizing}
              onResized={itemResized}
              moveResizeValidator={moveResizeValidator}
              onDrag={itemDrag}
              onDrop={itemDrop}
              onItemDoubleClick={onItemDoubleClick}
              onContextMenu={onItemContextMenu}
              onItemTap={onItemTap}
              onSelect={itemSelect}
              itemRenderer={itemRenderer}
              scrollRef={scrollRef}
              getTimelineContext={getTimelineContext}
              timeRatio={timeRatio}
              pixelsPerMs={pixelsPerMs}
            />
          ))}
      </div>
    );
  }
}
